import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ServiceCreateModal, { ServiceFormValues } from "../components/service-create-modal";
import ServiceStatusConfirmModal from "../components/service-status-confirm-modal";
import ServiceDetailModalProps from "../components/service-detail-modal";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import ServiceService, {
  Service,
  ServiceListFilters,
  ServiceStatusCode,
  CreateServicePayload,
} from "../services/service-service";
import ServiceTable, { ServiceRow } from "../components/service-table";
import { ALL_BRANCHES_VALUE, useCatalogBranchOptions } from "../../../hooks/use-catalog-branch-options";
import { ServiceVariantsService } from "../../service-variants/services/service-variant-service";

interface Row {
  id: number;
  name: string;
  code: string;
  storeName: string;
  branchId?: number;
  imageUrl?: string;
  statusCode?: number;
  status: "active" | "inactive" | "deleted";
}

const mapStatus = (s: number): Row["status"] => {
  return (s === 1 ? "active" : s === 2 ? "deleted" : "inactive");
};

export const ServiceIndexPage: React.FC = () => {
  // Stores từ API
  // Có thêm lựa chọn "Dùng chung" (branch_id = 0)
  const { options: storeOptions, branchLabel } = useCatalogBranchOptions();
  // Tạm cố định "Dùng chung" (branch_id = 0) — danh mục đang vận hành dùng chung cho mọi chi nhánh.
  // Mở lại bộ lọc: đổi về useState<number | null>(null) + bỏ comment effect chọn chi nhánh mặc định và khối bộ lọc "Chi nhánh".
  const [selectedbranchId] = useState<number | null>(ALL_BRANCHES_VALUE);
  const [detailRow, setDetailRow] = useState<ServiceRow | null>(null);
  const [detailData, setDetailData] = useState<Service | undefined>();
  const [rows, setRows] = useState<Row[]>([]);
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<Row | null>(null);
  type PendingStatus = { row: Row; target: "active" | "inactive" };
  const [pendingStatus, setPendingStatus] = useState<PendingStatus | null>(null);
  const { fetch: fetchDetail, loading: loadingDetail } = ServiceService.detail();
  const { fetch: fetchServices, loading } = ServiceService.list();
  const { fetch: createService, loading: creating } = ServiceService.create();
  const { fetch: update, loading: updating } = ServiceService.update();
  const { fetch: changeStatus, loading: changingStatus } = ServiceService.changeStatus();
  const listRef = useRef(fetchServices);
  const { fetch: fetchVariantsActive } = ServiceVariantsService.listActive();

  useEffect(() => {
    listRef.current = fetchServices;
  }, [fetchServices]);
  // Chọn chi nhánh mặc định — tạm tắt, đang cố định "Dùng chung"
  // useEffect(() => {
  //   // So với null (không dùng !) — 0 là "Dùng chung", một lựa chọn hợp lệ
  //   if (selectedbranchId === null && storeOptions.length > 0) {
  //     setSelectedbranchId(Number(storeOptions[0].value));
  //   }
  // }, [storeOptions, selectedbranchId]);
  const activeRows = useMemo(() => rows.filter((r) => r.statusCode === 1), [rows]);
  const inactiveRows = useMemo(() => rows.filter((r) => r.statusCode === 0), [rows]);

  const load = useCallback(async () => {
    if (selectedbranchId === null) return; // chưa chọn chi nhánh thì thoát luôn
    const params: ServiceListFilters = { status: -1,branch_id: selectedbranchId };
    const res = await listRef.current(params);
    if (res && isSuccess(res)) {
       // Lấy services
      // Gọi 1 lần lấy tất cả variants
      const variantRes = await fetchVariantsActive({ status: 1 }); // chỉ lấy variant đang hoạt động
      const variantMap: Record<number, string> = {};

      if (variantRes && isSuccess(variantRes) && Array.isArray(variantRes.data)) {
        variantRes.data.forEach((v: any) => {
          variantMap[v.id] = v.name;
        });
      } else {
        console.warn("Không có dữ liệu biến thể hợp lệ:", variantRes);
      }
      setRows(
        res.data.list.map((d: Service) => ({
          id: d.id,
          name: d.name,
          code: d.code,
          branchId: d.branch_id,
          storeName: branchLabel(d.branch_id),
          imageUrl: d.image_url,
          statusCode: Number(d.status),
          status: mapStatus(Number(d.status)),
          variantNames: d.service_variant_id ? [variantMap[d.service_variant_id]] : [],
        }))
      );
    }
  }, [selectedbranchId, branchLabel]);

  useEffect(() => { void load(); }, [load]);

  const onCreate = async (v: ServiceFormValues): Promise<boolean> => {
    const variantNames = (v.variant_names ?? [])
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    const payload: CreateServicePayload = {
      branch_id: Number(v.branchId),
      code: v.code.trim().toUpperCase(),
      name: v.name.trim(),
      description: v.description?.trim(),
      image_url: v.imageUrl?.trim(),
      status: v.isActive ? ServiceStatusCode.ACTIVE : ServiceStatusCode.INACTIVE,
      variant_names: variantNames.length > 0 ? variantNames : undefined,
    };
    const res = await createService(payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Tạo sản phẩm thành công", type: "success" });
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể tạo sản phẩm", type: "error" });
    return false;
  };

  // Quyền tạo / sửa / bật-tắt do BE kiểm tra (product service: @Privileges trên ServiceController) —
  // web không tự chặn nữa, thiếu quyền thì hiện thông báo lỗi BE trả về.
  const onEditOpen = (row: Row) => {
    setEditRow(row);
  };
   const handleViewDetail = async (row: ServiceRow) => {
       setDetailRow(row);
       setDetailData(undefined);
   
      try {
         const response = await fetchDetail(row.id);
        if (!response) {
          showToast({
            message: "Không thể tải chi tiết dịch vụ",
            type: "error",
          });
          setDetailRow(null);
          return;
        }

        if (isSuccess(response)) {
          setDetailData(response.data);
        } else {
          showToast({
            message: response.message ?? "Không thể tải chi tiết dịch vụ",
            type: "error",
          });
          setDetailRow(null);
        }
      } catch (error) {
        showToast({
          message:
            error instanceof Error
              ? error.message
              : "Không thể tải chi tiết dịch vụ",
          type: "error",
        });
        setDetailRow(null);
      }
    };
   
     const handleCloseDetailModal = () => {
       setDetailRow(null);
       setDetailData(undefined);
     };

  const onEditSubmit = async (v: ServiceFormValues): Promise<boolean> => {
    if (!editRow) return false;
    const payload = {
      code: v.code.trim().toUpperCase(),
      name: v.name.trim(),
      description: v.description?.trim(),
      image_url: v.imageUrl?.trim(),
      status: v.isActive ? ServiceStatusCode.ACTIVE : ServiceStatusCode.INACTIVE,
    };
    const res = await update(editRow.id, payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Cập nhật sản phẩm thành công", type: "success" });
      setEditRow(null);
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật sản phẩm", type: "error" });
    return false;
  };

  const onConfirmChangeStatus = async () => {
    if (!pendingStatus) return;

      const { row, target } = pendingStatus;
      const next = target === "active"
        ? ServiceStatusCode.ACTIVE
        : ServiceStatusCode.INACTIVE;

      const res = await changeStatus(row.id, { status: next });
  
    if (res && isSuccess(res)) {
      showToast({
        message: pendingStatus.target === "active"
          ? "Đã kích hoạt dịch vụ"
          : "Đã ngưng hoạt động dịch vụ",
        type: "success",
      });
      setPendingStatus(null);
      void load();
    } else {
      showToast({
        message: res?.message ?? "Không thể cập nhật trạng thái",
        type: "error",
      });
      setPendingStatus(null);
    }
  };
  

  return (
    <div className="">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold uppercase tracking-[0.3em] text-green-500 pl-2">Các sản phẩm giặt ủi</h1>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
        <div className="flex flex-col gap-3 rounded-2xl bg-slate-100 px-6 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-8">
            <button onClick={() => setTab("active")} className={`relative flex items-center gap-2 pb-2 text-sm font-semibold uppercase tracking-wide transition ${tab === "active" ? "text-green-600" : "text-slate-400 hover:text-slate-600"}`}>
              <span>Đang hoạt động</span>
              <span className={`${tab === "active" ? "bg-green-100 text-green-600" : "bg-slate-200 text-slate-500"} rounded-full px-2.5 py-0.5 text-xs font-semibold`}>{activeRows.length}</span>
              {tab === "active" ? <span className="pointer-events-none absolute inset-x-0 -bottom-1 h-1 rounded-full bg-green-500"></span> : null}
            </button>
            <button onClick={() => setTab("inactive")} className={`relative flex items-center gap-2 pb-2 text-sm font-semibold uppercase tracking-wide transition ${tab === "inactive" ? "text-green-600" : "text-slate-400 hover:text-slate-600"}`}>
              <span>Tạm ngưng</span>
              <span className={`${tab === "inactive" ? "bg-green-100 text-green-600" : "bg-slate-200 text-slate-500"} rounded-full px-2.5 py-0.5 text-xs font-semibold`}>{inactiveRows.length}</span>
              {tab === "inactive" ? <span className="pointer-events-none absolute inset-x-0 -bottom-1 h-1 rounded-full bg-green-500"></span> : null}
            </button>
          </div>
          {/* Bộ lọc chi nhánh — tạm ẩn, đang cố định "Dùng chung" (branch_id = 0)
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-600">Chi nhánh:</label>
            <select
              value={selectedbranchId ?? ""}
              onChange={(e) => setSelectedbranchId(Number(e.target.value))}
            >
              {loadingStores && <option>Đang tải...</option>}
              {!loadingStores && storeOptions.length === 0 && <option>Không có chi nhánh</option>}
              {storeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div> */}
        </div>

        <div className="mt-6">
          {tab === "active" ? (
            <ServiceTable
              rows={activeRows}
              isLoading={loading}
              startIndex={1}
              onEdit={onEditOpen}
              onViewDetail={handleViewDetail}
            />
          ) : (
            <ServiceTable
              rows={inactiveRows}
              isLoading={loading}
              startIndex={1}
              onEdit={onEditOpen}
              onViewDetail={handleViewDetail}
            />
          )}
        </div>
      </div>

      {open && selectedbranchId !== null ? (
        <ServiceCreateModal
          storeOptions={[
            ...storeOptions.filter((o) => Number(o.value) === selectedbranchId),
            ...storeOptions.filter((o) => Number(o.value) !== selectedbranchId),
          ]}
          initialValues={{ branchId: selectedbranchId }}
          onClose={() => setOpen(false)}
          onSubmit={onCreate}
          isSubmitting={creating}
        />
      ) : null}
      {editRow ? (
        <ServiceCreateModal
          storeOptions={storeOptions}
          onClose={() => setEditRow(null)}
          onSubmit={onEditSubmit}
          isSubmitting={updating}
          titleText="Cập nhật sản phẩm giặt ủi"
          submitText="Cập nhật"
          hideStoreSelect
          initialValues={{
            code: editRow.code,
            name: editRow.name,
            branchId: editRow.branchId ?? storeOptions[0]?.value,
            description: undefined,
            isActive: (editRow.statusCode ?? 1) === 1,
            imageUrl: editRow.imageUrl,
          }}
        />
      ) : null}

      {pendingStatus ? (
        <ServiceStatusConfirmModal
          mode={pendingStatus.target === "inactive" ? "deactivate" : "activate"}
          serviceName={pendingStatus.row.name}
          onCancel={() => setPendingStatus(null)}
          onConfirm={onConfirmChangeStatus}
          isProcessing={changingStatus} onViewDetail={function (): void {
            throw new Error("Function not implemented.");
          } }        />
      ) : null}
      {detailRow ? (
        <ServiceDetailModalProps
          rowName={detailRow.name}
          data={detailData}
          loading={loadingDetail}
          onClose={handleCloseDetailModal}
        />
      ) : null}
    </div>
  );
};

export default ServiceIndexPage;
